import { Component } from '@theme/component';

/**
 * @typedef {HTMLElement & { openModal?: (event?: Event) => void }} SearchDrawerModalElement
 */

/**
 * Search action trigger with progressive enhancement.
 * Falls back to a search page link when the drawer modal is unavailable.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} link - Fallback search page link.
 * @property {HTMLButtonElement} button - Modal trigger button.
 *
 * @extends Component<Refs>
 */
class SearchButton extends Component {
  /** @type {boolean} */
  #isEnhanced = false;

  connectedCallback() {
    super.connectedCallback();

    requestIdleCallback(() => {
      this.#init();
    });
  }

  disconnectedCallback() {
    this.#unbindEvents();
    super.disconnectedCallback();
  }

  /**
   * Waits for the search drawer modal, then swaps link for button.
   */
  async #init() {
    if (this.#isEnhanced) return;

    if (document.readyState === 'loading') {
      await new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
      });
    }

    const searchDrawerModal = /** @type {SearchDrawerModalElement | null} */ (
      document.getElementById('search-drawer-modal')
    );

    if (!searchDrawerModal) return;

    await customElements.whenDefined('search-drawer-modal');

    if (typeof searchDrawerModal.openModal !== 'function') return;

    this.#activateButton();
    this.#bindEvents();
    this.#isEnhanced = true;
  }

  /**
   * Shows the modal trigger and hides the fallback link.
   */
  #activateButton() {
    this.refs.link?.setAttribute('hidden', '');
    this.refs.button?.removeAttribute('hidden');
  }

  #bindEvents() {
    this.refs.button?.addEventListener('click', this.#handleClick);
  }

  #unbindEvents() {
    this.refs.button?.removeEventListener('click', this.#handleClick);
  }

  /**
   * Opens the search drawer modal.
   *
   * @param {MouseEvent} event - Click event.
   */
  #handleClick = (event) => {
    const searchDrawerModal = /** @type {SearchDrawerModalElement | null} */ (
      document.getElementById('search-drawer-modal')
    );

    if (!searchDrawerModal || typeof searchDrawerModal.openModal !== 'function') return;

    searchDrawerModal.openModal(event);
  };
}

if (!customElements.get('search-button')) {
  customElements.define('search-button', SearchButton);
}
